// react
import { useCallback, useMemo, useState } from 'react';
// third-party
import { useDispatch, useSelector } from 'react-redux';
// application
import { Fn, ThunkActionFn, ThunkReturnType } from './types';
import { IRootState } from './root/rootTypes';

export function useAppAction<T extends Fn>(
  action: T
): (
  ...args: Parameters<T>
) => T extends ThunkActionFn ? ThunkReturnType<T> : ReturnType<T> {
  const dispatch = useDispatch();
  return useCallback((...args: Parameters<T>) => dispatch(action(...args)), []);
}

export function useAppSelector<T extends (state: IRootState) => any>(
  selector: T
): ReturnType<T> {
  return useSelector(selector);
}

type AsyncActionFn = (...args: any[]) => Promise<any>;

export function useAsyncAction<T extends AsyncActionFn>(
  action: T,
  deps: any[] = []
): [(...args: Parameters<T>) => void, boolean] {
  const [loading, setLoading] = useState(false);

  const run = useMemo(
    () =>
      (...args: Parameters<T>) => {
        if (loading) {
          return;
        }

        setLoading(true);

        action(...args).then(() => {
          setLoading(false);
        });
      },
    [loading, setLoading, ...deps]
  );

  return useMemo(() => [run, loading], [run, loading]);
}
